/**
 * Formats: Constructed Standard (1v1, exactly 40 cards) and 2v2, which uses the same deck-construction
 * numbers with one more banned card.
 *
 * Standard is the current year's sets plus the previous year's (TR 601.3): Origins: Proving Grounds,
 * Origins, Spiritforged, Unleashed, Vendetta and Radiance (TR 601.3.c; Radiance from its release on
 * 2026-10-23, TR 601.3.c.6, and here from the preview). Promotional sets are legal by name (TR 601.2.a):
 * a card is legal when any printing of its name is in a legal set.
 */
export type FormatId = 'standard' | '2v2';

/** Deck-construction numbers and legal sets of a format. */
export interface FormatRules {
  id: FormatId;
  name: string;
  /** Sets whose cards are legal (by name, TR 601.2.a). */
  legalSets: readonly string[];
  /** Main Deck size including the Chosen Champion: exactly this many (TR 402.1). */
  mainSize: number;
  /** Copies per card name across champion, Main Deck and sideboard (CR 103.3.b; TR 403.3). */
  copyLimit: number;
  /** Signature cards in total (CR 103.3.d.1). */
  signatureLimit: number;
  runeCount: number;
  battlefieldCount: number;
  sideboardMax: number;
  /** Names banned in this format on top of the Standard list (live or static). */
  extraBans: readonly string[];
}

export const STANDARD: FormatRules = {
  id: 'standard',
  name: 'Standard',
  legalSets: ['OGS', 'OGN', 'SFD', 'UNL', 'VEN', 'RAD'],
  mainSize: 40,
  copyLimit: 3,
  signatureLimit: 3,
  runeCount: 12,
  battlefieldCount: 3,
  sideboardMax: 10,
  extraBans: [],
};

export const TWO_VS_TWO: FormatRules = {
  ...STANDARD,
  id: '2v2',
  name: '2v2',
  extraBans: ['Master Yi, Wuju Bladesman'],
};

export const FORMATS: Readonly<Record<FormatId, FormatRules>> = {
  standard: STANDARD,
  '2v2': TWO_VS_TWO,
};

/** `'2v2'` for a 2v2 deck, `'standard'` for anything else (Runebound treats a null format as Standard). */
export function formatIdOf(format: string | null | undefined): FormatId {
  return format === '2v2' ? '2v2' : 'standard';
}
